/**
 * useSourceApplications Hook
 * Custom hook for managing source generator application state
 *
 * @version 2025.1.0
 */

import { useState, useCallback } from 'react';
import { sourceApplicationService } from '../../../services/sourceApplicationService';
import {
  ApplicationLevel,
  SourceApplicationData,
  SourceWithApplication,
  AvailableSource,
  CriticalSourceExposureAlerts,
} from '../../../types/sourceApplication';

// ============================================================================
// TYPES
// ============================================================================

interface UseSourceApplicationsState {
  applied: SourceWithApplication[];
  available: AvailableSource[];
  alerts: CriticalSourceExposureAlerts | null;
  loading: boolean;
  loadingAlerts: boolean;
  error: string | null;
  alertsError: string | null;
}

interface UseSourceApplicationsReturn extends UseSourceApplicationsState {
  loadSources: (level: ApplicationLevel, targetId: number) => Promise<void>;
  loadAlerts: (companyId: number) => Promise<void>;
  applySource: (
    level: ApplicationLevel,
    targetId: number,
    sourceGeneratorId: number,
    data?: SourceApplicationData
  ) => Promise<boolean>;
  applyMultipleSources: (
    level: ApplicationLevel,
    targetId: number,
    sourceGeneratorIds: number[],
    data?: SourceApplicationData
  ) => Promise<boolean>;
  removeSource: (
    level: ApplicationLevel,
    targetId: number,
    sourceGeneratorId: number
  ) => Promise<boolean>;
  removeMultipleSources: (
    level: ApplicationLevel,
    targetId: number,
    sourceGeneratorIds: number[]
  ) => Promise<boolean>;
  updateApplication: (
    level: ApplicationLevel,
    targetId: number,
    sourceGeneratorId: number,
    data: SourceApplicationData
  ) => Promise<boolean>;
  clearError: () => void;
  clearAlertsError: () => void;
}

// ============================================================================
// HOOK
// ============================================================================

export function useSourceApplications(): UseSourceApplicationsReturn {
  const [state, setState] = useState<UseSourceApplicationsState>({
    applied: [],
    available: [],
    alerts: null,
    loading: false,
    loadingAlerts: false,
    error: null,
    alertsError: null,
  });

  /**
   * Load sources for a target
   */
  const loadSources = useCallback(
    async (level: ApplicationLevel, targetId: number): Promise<void> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const response = await sourceApplicationService.getForTarget(level, targetId);
        setState((prev) => ({
          ...prev,
          applied: response.applied,
          available: response.available,
          loading: false,
        }));
      } catch (err: unknown) {
        const error = err as { message?: string };
        setState((prev) => ({
          ...prev,
          loading: false,
          error: error.message ?? 'Erro ao carregar fontes geradoras',
        }));
      }
    },
    []
  );

  /**
   * Load critical exposure alerts for a company
   */
  const loadAlerts = useCallback(
    async (companyId: number): Promise<void> => {
      setState((prev) => ({ ...prev, loadingAlerts: true, alertsError: null }));

      try {
        const response = await sourceApplicationService.getAlertsForCompany(companyId);
        setState((prev) => ({
          ...prev,
          alerts: response,
          loadingAlerts: false,
        }));
      } catch (err: unknown) {
        const error = err as { message?: string };
        setState((prev) => ({
          ...prev,
          loadingAlerts: false,
          alertsError: error.message ?? 'Erro ao carregar alertas',
        }));
      }
    },
    []
  );

  /**
   * Apply a single source generator
   */
  const applySource = useCallback(
    async (
      level: ApplicationLevel,
      targetId: number,
      sourceGeneratorId: number,
      data?: SourceApplicationData
    ): Promise<boolean> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        await sourceApplicationService.applyToTarget(level, targetId, {
          sourceGeneratorId,
          ...data,
        });

        // Reload sources after applying
        const response = await sourceApplicationService.getForTarget(level, targetId);
        setState((prev) => ({
          ...prev,
          applied: response.applied,
          available: response.available,
          loading: false,
        }));

        return true;
      } catch (err: unknown) {
        const error = err as { message?: string };
        setState((prev) => ({
          ...prev,
          loading: false,
          error: error.message ?? 'Erro ao aplicar fonte geradora',
        }));
        return false;
      }
    },
    []
  );

  /**
   * Apply multiple source generators
   */
  const applyMultipleSources = useCallback(
    async (
      level: ApplicationLevel,
      targetId: number,
      sourceGeneratorIds: number[],
      data?: SourceApplicationData
    ): Promise<boolean> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        await sourceApplicationService.applyMultiple(level, targetId, sourceGeneratorIds, data);

        // Reload sources after applying
        const response = await sourceApplicationService.getForTarget(level, targetId);
        setState((prev) => ({
          ...prev,
          applied: response.applied,
          available: response.available,
          loading: false,
        }));

        return true;
      } catch (err: unknown) {
        const error = err as { message?: string };
        setState((prev) => ({
          ...prev,
          loading: false,
          error: error.message ?? 'Erro ao aplicar fontes geradoras',
        }));
        return false;
      }
    },
    []
  );

  /**
   * Remove a single source generator
   */
  const removeSource = useCallback(
    async (
      level: ApplicationLevel,
      targetId: number,
      sourceGeneratorId: number
    ): Promise<boolean> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        await sourceApplicationService.removeFromTarget(level, targetId, sourceGeneratorId);

        // Reload sources after removing
        const response = await sourceApplicationService.getForTarget(level, targetId);
        setState((prev) => ({
          ...prev,
          applied: response.applied,
          available: response.available,
          loading: false,
        }));

        return true;
      } catch (err: unknown) {
        const error = err as { message?: string };
        setState((prev) => ({
          ...prev,
          loading: false,
          error: error.message ?? 'Erro ao remover fonte geradora',
        }));
        return false;
      }
    },
    []
  );

  /**
   * Remove multiple source generators
   */
  const removeMultipleSources = useCallback(
    async (
      level: ApplicationLevel,
      targetId: number,
      sourceGeneratorIds: number[]
    ): Promise<boolean> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        await sourceApplicationService.removeMultiple(level, targetId, sourceGeneratorIds);

        // Reload sources after removing
        const response = await sourceApplicationService.getForTarget(level, targetId);
        setState((prev) => ({
          ...prev,
          applied: response.applied,
          available: response.available,
          loading: false,
        }));

        return true;
      } catch (err: unknown) {
        const error = err as { message?: string };
        setState((prev) => ({
          ...prev,
          loading: false,
          error: error.message ?? 'Erro ao remover fontes geradoras',
        }));
        return false;
      }
    },
    []
  );

  /**
   * Update an application
   */
  const updateApplication = useCallback(
    async (
      level: ApplicationLevel,
      targetId: number,
      sourceGeneratorId: number,
      data: SourceApplicationData
    ): Promise<boolean> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        await sourceApplicationService.updateForTarget(level, targetId, sourceGeneratorId, data);

        // Reload sources after updating
        const response = await sourceApplicationService.getForTarget(level, targetId);
        setState((prev) => ({
          ...prev,
          applied: response.applied,
          available: response.available,
          loading: false,
        }));

        return true;
      } catch (err: unknown) {
        const error = err as { message?: string };
        setState((prev) => ({
          ...prev,
          loading: false,
          error: error.message ?? 'Erro ao atualizar aplicação',
        }));
        return false;
      }
    },
    []
  );

  /**
   * Clear main error
   */
  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  /**
   * Clear alerts error
   */
  const clearAlertsError = useCallback(() => {
    setState((prev) => ({ ...prev, alertsError: null }));
  }, []);

  return {
    ...state,
    loadSources,
    loadAlerts,
    applySource,
    applyMultipleSources,
    removeSource,
    removeMultipleSources,
    updateApplication,
    clearError,
    clearAlertsError,
  };
}

export default useSourceApplications;
